/**
 * One form's columns in an export template: the form's available columns on
 * the left, the chosen ones on the right (order = the order in the file).
 *
 * Drag and drop, the same HTML5 approach as Customize mode (and its drag
 * payload helpers):
 *   - drag a row by its ⋮⋮ grip to reorder it (a line shows where it lands);
 *   - drag an available column onto the table to add it at that position;
 *   - drag a chosen column back onto the list to remove it;
 *   - keyboard: focus ⋮⋮ and press ↑ / ↓ (announced to screen readers);
 *   - touch screens get ↑ ↓ buttons instead (HTML drag-and-drop is unreliable
 *     on phones).
 * A column moves only within its own form's table.
 */
import { useMemo, useRef, useState } from 'react'
import type { CSSProperties, DragEvent, KeyboardEvent } from 'react'
import { ArrowDown, ArrowUp, GripVertical, Plus, X } from 'lucide-react'
import { setDragData } from '../../customize/ui'
import { tw } from '../../testHooks'
import { input, meta, tableStyle, th, td } from '../../messaging/ui'
import type { ExportCatalogColumn, ExportCatalogForm, ExportTemplateColumn, ValueMode } from './types'
import { insertAt, moveItem } from './types'

type DragSrc = { kind: 'move'; index: number } | { kind: 'add'; key: string }

const coarse = typeof window !== 'undefined' && !!window.matchMedia
  && window.matchMedia('(pointer: coarse)').matches

const ACCENT = 'var(--color-accent, #0061D0)'

export function ColumnTable({ form, cols, onChange, isHeader, readOnly }: {
  form: ExportCatalogForm
  cols: ExportTemplateColumn[]
  onChange: (cols: ExportTemplateColumn[]) => void
  isHeader: boolean
  readOnly?: boolean
}) {
  const [search, setSearch] = useState('')
  const [drop, setDrop] = useState<{ index: number; before: boolean } | null>(null)
  const [overList, setOverList] = useState(false)
  const [said, setSaid] = useState('')
  const src = useRef<DragSrc | null>(null)
  const byKey = useMemo(() => new Map(form.columns.map(c => [c.key, c])), [form])
  const used = new Set(cols.map(c => c.column))
  const q = search.trim().toLowerCase()
  const available = form.columns.filter(c => !used.has(c.key)
    && (!q || c.label.toLowerCase().includes(q) || c.key.includes(q)))

  const announce = (msg: string) => { setSaid(''); window.setTimeout(() => setSaid(msg), 30) }
  const labelOf = (key: string) => byKey.get(key)?.label || key
  const newCol = (c: ExportCatalogColumn): ExportTemplateColumn => ({
    column: c.key, heading: c.label, value: 'display', ...(isHeader ? { key: c.pk } : {}),
  })
  const add = (c: ExportCatalogColumn, at = cols.length) => {
    onChange(insertAt(cols, newCol(c), at))
    announce(`${c.label} added at position ${at + 1}.`)
  }
  const patch = (i: number, p: Partial<ExportTemplateColumn>) =>
    onChange(cols.map((c, j) => (j === i ? { ...c, ...p } : c)))
  const remove = (i: number) => {
    announce(`${labelOf(cols[i].column)} removed.`)
    onChange(cols.filter((_, j) => j !== i))
  }
  const move = (i: number, j: number) => {
    if (j < 0 || j >= cols.length) return
    const next = cols.slice()
    ;[next[i], next[j]] = [next[j], next[i]]
    onChange(next)
    announce(`${labelOf(cols[i].column)} moved to position ${j + 1} of ${cols.length}.`)
  }

  // ── drag and drop ──
  const startMove = (e: DragEvent, index: number) => {
    src.current = { kind: 'move', index }
    setDragData(e, `export|${form.form_no}|move|${index}`)
    const row = (e.currentTarget as HTMLElement).closest('tr')
    if (row) e.dataTransfer.setDragImage(row, 18, 18)
  }
  const startAdd = (e: DragEvent, key: string) => {
    src.current = { kind: 'add', key }
    setDragData(e, `export|${form.form_no}|add|${key}`)
  }
  const overRow = (e: DragEvent, index: number) => {
    if (!src.current) return
    e.preventDefault()
    const box = (e.currentTarget as HTMLElement).getBoundingClientRect()
    setDrop({ index, before: e.clientY < box.top + box.height / 2 })
  }
  const dropOn = (e: DragEvent, target: number) => {
    e.preventDefault()
    const s = src.current
    src.current = null
    setDrop(null)
    if (!s) return
    if (s.kind === 'add') {
      const c = byKey.get(s.key)
      if (c && !used.has(c.key)) add(c, target)
      return
    }
    const to = s.index < target ? target - 1 : target
    if (to === s.index) return
    onChange(moveItem(cols, s.index, target))
    announce(`${labelOf(cols[s.index].column)} moved to position ${to + 1} of ${cols.length}.`)
  }
  const dropOnList = (e: DragEvent) => {
    e.preventDefault()
    const s = src.current
    src.current = null
    setOverList(false)
    if (s?.kind === 'move') remove(s.index)
  }
  const endDrag = () => { src.current = null; setDrop(null); setOverList(false) }
  const gripKey = (e: KeyboardEvent, i: number) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return
    e.preventDefault()
    const j = e.key === 'ArrowUp' ? i - 1 : i + 1
    move(i, j)
    window.setTimeout(() => {
      document.querySelector<HTMLElement>(`[data-export-grip="${form.form_no}|${j}"]`)?.focus()
    }, 0)
  }

  const lineStyle = (i: number): CSSProperties => {
    if (!drop || drop.index !== i) return {}
    return { boxShadow: `inset 0 ${drop.before ? 3 : -3}px 0 ${ACCENT}` }
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
      <div onDragOver={(e) => { if (src.current?.kind === 'move') { e.preventDefault(); setOverList(true) } }}
        onDragLeave={() => setOverList(false)} onDrop={dropOnList}
        {...tw('group', `export-template:available:${form.form_no}`)}
        style={{ flex: '0 1 240px', minWidth: 200, border: `1px solid ${overList ? ACCENT : 'var(--color-border, #d8dee6)'}`, borderRadius: 8,
          overflow: 'hidden', background: overList ? 'var(--color-accent-weak, rgba(0,97,208,.10))' : undefined }}>
        <div style={{ ...th, display: 'block' }}>
          Columns of {form.label}
          {!readOnly && <div style={{ fontWeight: 400 }}>Drag one into the table at the place you want</div>}
        </div>
        <div style={{ padding: 8 }}>
          <input style={{ ...input, width: '100%' }} value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Find a column" aria-label={`Find a column of ${form.label}`} disabled={readOnly} />
        </div>
        <ul style={{ listStyle: 'none', margin: 0, padding: '0 0 6px', maxHeight: 260, overflow: 'auto' }}>
          {available.map(c => (
            <li key={c.key} draggable={!readOnly} onDragStart={(e) => startAdd(e, c.key)} onDragEnd={endDrag}
              title={readOnly ? undefined : 'Drag into the table, or press Add'}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
                padding: '4px 10px', fontSize: 14, cursor: readOnly ? 'default' : 'grab' }}>
              <span style={{ minWidth: 0 }}>{c.label} <Tag col={c} /></span>
              <button type="button" className="btn btn-secondary btn-sm" disabled={readOnly}
                onClick={() => add(c)} aria-label={`Add ${c.label}`} {...tw('action', `export-template:add:${c.key}`)}>
                <Plus size={13} /> Add
              </button>
            </li>
          ))}
          {!available.length && <li style={{ ...meta, padding: '6px 10px' }}>All columns are in the template.</li>}
        </ul>
      </div>

      <div style={{ flex: '1 1 420px', minWidth: 0 }}>
        <div style={{ overflowX: 'auto', border: '1px solid var(--color-border, #d8dee6)', borderRadius: 8 }}>
          <table style={{ ...tableStyle, minWidth: 560 }}>
            <thead>
              <tr>
                <th style={{ ...th, width: coarse ? 72 : 40 }}><span style={srOnly}>Move</span></th>
                <th style={th}>Column</th>
                <th style={th}>Heading in the file</th>
                {isHeader && <th style={{ ...th, textAlign: 'center' }} title="Identifies a record. Repeated on line rows.">Key</th>}
                <th style={th}>Value</th>
                <th style={th}><span style={srOnly}>Remove</span></th>
              </tr>
            </thead>
            <tbody>
              {cols.map((c, i) => {
                const def = byKey.get(c.column)
                return (
                  <tr key={`${c.column}-${i}`} onDragOver={(e) => overRow(e, i)}
                    onDrop={(e) => dropOn(e, drop && drop.index === i && !drop.before ? i + 1 : i)}
                    {...tw('field', `export-template:column:${form.form_no}:${c.column}`)}
                    style={{ ...lineStyle(i), opacity: src.current?.kind === 'move' && src.current.index === i ? 0.4 : 1 }}>
                    <td style={{ ...td, verticalAlign: 'middle', whiteSpace: 'nowrap', textAlign: 'center' }}>
                      {!readOnly && (coarse ? (
                        <>
                          <button type="button" className="btn btn-secondary btn-sm" disabled={i === 0}
                            onClick={() => move(i, i - 1)} aria-label={`Move ${labelOf(c.column)} up`}><ArrowUp size={13} /></button>
                          <button type="button" className="btn btn-secondary btn-sm" disabled={i === cols.length - 1}
                            onClick={() => move(i, i + 1)} aria-label={`Move ${labelOf(c.column)} down`}><ArrowDown size={13} /></button>
                        </>
                      ) : (
                        <span role="button" tabIndex={0} draggable data-export-grip={`${form.form_no}|${i}`}
                          onDragStart={(e) => startMove(e, i)} onDragEnd={endDrag} onKeyDown={(e) => gripKey(e, i)}
                          title="Drag to reorder"
                          aria-label={`Move ${labelOf(c.column)}: drag it, or press the up and down arrow keys`}
                          style={{ display: 'inline-flex', padding: 4, borderRadius: 5, cursor: 'grab',
                            color: 'var(--color-text-faint, #687281)' }}>
                          <GripVertical size={15} />
                        </span>
                      ))}
                    </td>
                    <td style={{ ...td, verticalAlign: 'middle' }}>
                      <div>{def ? def.label : c.column} {def ? <Tag col={def} /> : <Tag missing />}</div>
                      <div style={{ ...meta, fontFamily: "'IBM Plex Mono', monospace" }}>{c.column}</div>
                    </td>
                    <td style={{ ...td, verticalAlign: 'middle' }}>
                      <input style={{ ...input, width: '100%', height: 32 }} value={c.heading} disabled={readOnly}
                        aria-label={`Heading for ${labelOf(c.column)}`}
                        onChange={e => patch(i, { heading: e.target.value })} />
                    </td>
                    {isHeader && (
                      <td style={{ ...td, verticalAlign: 'middle', textAlign: 'center' }}>
                        <input type="checkbox" checked={!!c.key} disabled={readOnly} aria-label={`${labelOf(c.column)} is a key column`}
                          onChange={e => patch(i, { key: e.target.checked })}
                          style={{ width: 16, height: 16, accentColor: ACCENT }} />
                      </td>
                    )}
                    <td style={{ ...td, verticalAlign: 'middle' }}>
                      {def?.coded ? (
                        <select style={{ ...input, height: 32 }} value={c.value} disabled={readOnly}
                          aria-label={`Value for ${labelOf(c.column)}`}
                          onChange={e => patch(i, { value: e.target.value as ValueMode })}>
                          <option value="display">{def.person ? 'Name' : 'As shown'}</option>
                          <option value="code">Stored code</option>
                          <option value="both">Both (2 columns)</option>
                        </select>
                      ) : <span style={meta}>As stored</span>}
                    </td>
                    <td style={{ ...td, verticalAlign: 'middle', textAlign: 'right' }}>
                      {!readOnly && (
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => remove(i)}
                          aria-label={`Remove ${labelOf(c.column)}`}><X size={13} /></button>
                      )}
                    </td>
                  </tr>
                )
              })}
              {!cols.length && (
                <tr onDragOver={(e) => { if (src.current) { e.preventDefault(); setDrop({ index: 0, before: true }) } }}
                  onDrop={(e) => dropOn(e, 0)}>
                  <td colSpan={isHeader ? 6 : 5} style={{ ...td, ...meta, padding: 14, ...lineStyle(0) }}>
                    No columns yet. Add them from the list, or drag one here.
                  </td>
                </tr>
              )}
              {cols.length > 0 && !readOnly && (
                <tr onDragOver={(e) => { if (src.current) { e.preventDefault(); setDrop({ index: cols.length, before: true }) } }}
                  onDrop={(e) => dropOn(e, cols.length)}>
                  <td colSpan={isHeader ? 6 : 5} style={{ padding: 0, height: 10, ...lineStyle(cols.length) }} />
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {!readOnly && (
          <p style={{ ...meta, margin: '6px 0 0' }}>
            Drag <b>⋮⋮</b> to change the order. Drag a column in from the list to place it at a position, or back to the
            list to remove it. With the keyboard, focus <b>⋮⋮</b> and press ↑ or ↓.
          </p>
        )}
        <div aria-live="polite" style={srOnly}>{said}</div>
      </div>
    </div>
  )
}

function Tag({ col, missing }: { col?: ExportCatalogColumn; missing?: boolean }) {
  const base: CSSProperties = { fontSize: 13, fontWeight: 600, borderRadius: 4, padding: '0 6px', marginLeft: 4 }
  if (missing) return <span style={{ ...base, background: 'rgba(214,138,0,.15)', color: 'var(--color-warning, #8a5300)' }}>no longer on the screen</span>
  if (col?.uda) return <span style={{ ...base, background: 'var(--color-surface-2, #f0f3f7)', color: 'var(--color-text-secondary, #465062)' }}>your field</span>
  if (col?.secure) return <span style={{ ...base, background: 'rgba(214,138,0,.15)', color: 'var(--color-warning, #8a5300)' }}>masked</span>
  return null
}

const srOnly: CSSProperties = {
  position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap',
  margin: -1, padding: 0, border: 0,
}
